import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import { translate } from "@td/shared_utils";
import { Link } from "@td/ui";
import { modalTypes, SERVER_DATE_FORMAT, CONSULTATION_DATE_FORMAT } from "../../constants";
import { Status, Priority } from "..";
import { TaskName, ActionButton, DueDate } from "./components";
import Assignees from "../../containers/Assignees";
import { useModals } from "../../contexts/modals-context";
import { TableRow, TableColumn } from "@td/ui";

const ListViewItem = React.memo(({ task }) => {
  const {
    category,
    consultation,
    member,
    assignees,
    status,
    priority,
    dueDate,
    restricted,
  } = task;
  const { isDeleted } = member;
  const { openModal } = useModals();

  const isTaskRestricted = isDeleted || restricted;

  const handleEditModalOpen = useCallback(() => {
    if (isTaskRestricted) return;

    openModal(modalTypes.EDIT_TASK_MODAL, { taskId: task.id });
  }, [openModal, task]);

  const formattedRequestDate = useMemo(() => {
    if (consultation) {
      const { requestDate, timeZone } = consultation;

      return !restricted
        ? moment.tz(requestDate, SERVER_DATE_FORMAT, timeZone).tz(moment.tz.guess()).format(CONSULTATION_DATE_FORMAT)
        : requestDate;
    }
  }, [consultation, restricted]);

  return (
    <TableRow data-task-id={task.id}>
      <TableColumn data-testid="list-view-item-column">
        <TaskName
          task={task}
          onClick={handleEditModalOpen}
          restricted={isTaskRestricted}
        />
      </TableColumn>
      <TableColumn data-testid="list-view-item-column">
        {translate(null, "care_team_tasks.categories", category.replace("TASKCATEGORY_", ""))}
      </TableColumn>
      <TableColumn data-testid="list-view-item-column">
        <Assignees assignees={assignees} />
      </TableColumn>
      <TableColumn data-testid="list-view-item-column">
        <Link
          target="_blank"
          restricted={isTaskRestricted}
          href={window.Routes.member_path(member.id)}
        >
          {member.firstAndLastName}
        </Link>
      </TableColumn>
      <TableColumn data-testid="list-view-item-column">
        {consultation && (
          <Link
            target="_blank"
            restricted={isTaskRestricted}
            href={window.Routes.consultation_path(consultation.id)}
          >
            {formattedRequestDate}
          </Link>
        )}
      </TableColumn>
      <TableColumn data-testid="list-view-item-column">
        {dueDate ? (
          <DueDate task={task} />
        ) : (
          "-"
        )}
      </TableColumn>
      {priority === "TASKPRIORITY_URGENT" ? (
        <TableColumn data-testid="list-view-item-column">
          <Priority
            style={{ margin: "-2px 0" }}
            value="URGENT"
          />
        </TableColumn>
      ) : (
        <TableColumn
          style={{
            alignItems: "center",
          }}
          data-testid="list-view-item-column"
        >
          -
        </TableColumn>
      )}
      <TableColumn data-testid="list-view-item-column">
        <Status
          style={{ margin: "-2px 0" }}
          value={status.replace("TASKSTATUS_", "")}
        />
      </TableColumn>
      <TableColumn data-testid="list-view-item-column">
        {!isTaskRestricted && <ActionButton onClick={handleEditModalOpen} />}
      </TableColumn>
    </TableRow>
  );
});

ListViewItem.propTypes = {
  task: PropTypes.shape({
    name: PropTypes.string,
    typeCd: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    consultation: PropTypes.shape({
      id: PropTypes.string.isRequired,
      requestDate: PropTypes.string.isRequired,
      timeZone: PropTypes.string.isRequired,
    }),
    member: PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstAndLastName: PropTypes.string.isRequired
    }),
    assignees: PropTypes.array.isRequired,
    status: PropTypes.string.isRequired,
    priority: PropTypes.string.isRequired,
    dueDate: PropTypes.string,
  }),
};

export default ListViewItem;
